import Box from '@mui/material/Box';
import { NotificationIcon } from '../notificationIcon';
import {
  NotificationCancelButton,
  NotificationCancelButtonType,
} from '../notificationCancelButton';
import { NotificationSubtitle } from '../notificationSubtitle';
import { NotificationTitle } from '../notificationTitle';
import { useToastNotificationStyles } from './toastNotification.styles';
import { useBounds } from '@doxyme/ui-foundation/hooks/useBounds';
import { ThemeProvider, useTheme } from '@doxyme/ui-foundation/theme';
import { type JSX } from 'react';
import { useViewport } from '@doxyme/ui-foundation/hooks';
import { Button } from '@doxyme/ui-foundation/components/button';

export type ToastNotificationProps = {
  id?: string | number;
  title: string;
  subtitle?: string;
  icon: JSX.Element;
  actionLink?: {
    label: string;
    onClick: () => void;
  };
  onDismiss: () => void;
  onCancel?: () => void;
};

export const ToastNotification = ({
  id,
  title,
  subtitle,
  icon,
  actionLink,
  onDismiss,
  onCancel,
}: ToastNotificationProps): JSX.Element => {
  const theme = useTheme('dark');
  const css = useToastNotificationStyles();
  const { width: viewportWidth } = useBounds();
  const { isSmallViewport } = useViewport();

  const externalHorizontalPadding = Number(theme.spacing(3).slice(0, -2)) * 2;

  /*
    Toast notifications have a fixed width of 366px to allow for stacking in the future.
    On some smaller devices this exceeds the viewport when taking the external padding
    into account, so we reduce the width to cater for this ensuring the external padding remains.
  */
  const width = isSmallViewport
    ? '100%'
    : viewportWidth - externalHorizontalPadding > 366
      ? '366px'
      : `${viewportWidth - externalHorizontalPadding}px`;

  return (
    <ThemeProvider themeName='dark'>
      <Box
        css={css.container}
        sx={{
          width: width,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              gap: 2,
              flex: '1 1 0',
              minWidth: 0,
            }}
          >
            <NotificationIcon icon={icon} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 0',
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <NotificationTitle title={title} />

                <Box
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  <NotificationCancelButton
                    notificationId={id?.toString() ?? ''}
                    type={NotificationCancelButtonType.Icon}
                    dismissNotification={onCancel || (() => onDismiss())}
                  />
                </Box>
              </Box>

              {subtitle && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <NotificationSubtitle subtitle={subtitle} />
                  {actionLink && (
                    <Button
                      type='link'
                      size='xsmall'
                      css={{ minHeight: 0, flex: 'initial', flexShrink: 0 }}
                      onClick={actionLink.onClick}
                    >
                      {actionLink.label}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
